import React from 'react';
import { Paper, Stack, Typography, Box, Button, IconButton } from '@mui/material'
import { Menu, MenuItem, MenuList, Tooltip } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { depthToColor, sensorToColor } from '../../utils/layer_style'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import type { GeoJsonFeature } from '../../utils/ogc';

// getLineColor: [200, 200, 240, 200],

function Swatch({ color, radius=9 }: { color: number[]; radius?: number }) {
  const [r, g, b] = color
  return <Box sx={{ 
    width: { xs: 9, md: radius }, height: { xs: 9, md: radius }, textAlign: 'center', borderRadius: '100%', 
    bgcolor: `rgb(${r} ${g} ${b})`, 
    border: `${Math.min(radius/8, 3)}px solid rgba(200, 200, 240, 0.55)`, 
    margin: '0 auto' 
  }} />
}

const depthToRadius = (depth: number) => {
  return Math.round(depth ? 16 + 2 * depth / 24 : 8);
}

function Legend({ label, legendMode, setLegendMode, simpleMode }: { label?: React.ReactNode, legendMode: string, setLegendMode: any, simpleMode?: boolean }) {
  const [open, setOpen] = React.useState(true);
  const sensor = (status: string) => sensorToColor({ properties: { fk_status: status } } as unknown as GeoJsonFeature);

  const theme = useTheme()
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'))

  React.useEffect(() => {
    setOpen(!isSmDown)
  }, [isSmDown])

  const depthBins = [
    { label: 'None', color: depthToColor(0), radius: 9 },
    { label: 'Minimal (< 4")', color: depthToColor(2), radius: depthToRadius(2) },
    { label: 'Minor (4-12")', color: depthToColor(8), radius: depthToRadius(8) },
    { label: 'Moderate (12-24")', color: depthToColor(16), radius: depthToRadius(16) },
    { label: 'Major (≥ 24")', color: depthToColor(30), radius: depthToRadius(30) },
    // { label: 'Hidden', color: sensor('hidden') },
    simpleMode ? null : { label: 'Offline', color: sensor('offline') },
    simpleMode ? null : { label: 'Retired', color: sensor('retired') },
  ].filter(Boolean) as { label: string; color: number[]; radius?: number }[];

  return (
    <Paper elevation={open ? 6 : 0} sx={{ 
        // p: 1.5, borderRadius: 2, minWidth: 140 
        // transition: open ? 'width 0.3s ease' : null,
        // borderRadius: { xs: open ? 1 : 2, md: 2 },
        // width: open ? { xs: '120px', md: '140px' } : '40px',
        p: { xs: 1, md: 1 },
        minWidth: { xs: 40 },
        // opacity: open ? 1 : 0.6,
        // bgcolor: open ? 'background.paper' : ,
      }}>
      <Collapse in={open} orientation='horizontal'>
      <Stack spacing={0} sx={{
        width: { xs: '120px', md: '140px' },
        // p: { xs: 1, md: 1.5 },
      }}>
        <Box sx={{ minHeight: 22 }}>
          {open ? (
            typeof label === 'string' ? (
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{label}</Typography>
            ) : (
              <LegendLabelMenu 
                // timeRange={timeRange} 
                // currentTime={currentTime} 
                // setCurrentTime={setCurrentTime} 
                legendMode={legendMode}
                setLegendMode={setLegendMode}
              />
            )
          ) : null}
        </Box>
        <Collapse in={open}>
        <Box component="table" sx={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 3px' }}>
          <Box component="tbody">
            {depthBins.map((b) => (
              <Box component="tr" key={b.label} sx={{ verticalAlign: 'middle' }}>
                <Box component="td" sx={{ textAlign: 'center' }}>
                  <Swatch color={b.color} radius={b.radius} />
                </Box>
                <Box component="td">
                  <Typography variant="body2" sx={{ fontSize: { xs: 13, md: 14 } }}>{b.label}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
        </Collapse>
        {/* <Divider sx={{ my: 0.5 }} /> */}
      </Stack>
      </Collapse>
      <IconButton color='default' sx={{ 
          position: 'absolute', 
          // bottom: 0, left: '50%', transform: 'translate(-50%, 50%)', 
          bottom: 0, right: 0,
          outline: 'none !important', 
          '&:hover': { bgcolor: 'background.paper' },
        }} onClick={() => setOpen(!open)}>
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </IconButton>
    </Paper>
  )
}
export default React.memo(Legend);


export const LegendLabelMenu = React.memo(({ legendMode, setLegendMode }: { legendMode: 'max' | 'overTime'; setLegendMode: (mode: 'max' | 'overTime') => void }) => {


    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
    const label = legendMode === 'max' ? 'Max Flood Depth' : 'Flood Depth Over Time'
    return (
      <>
        <Tooltip title="What should we show?">
          <Button size="small" variant="text" onClick={(e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget)} sx={{ textAlign: 'left', textTransform: 'none', fontSize: { xs: 14, md: 15 }, borderColor: 'divider', '&:hover': { bgcolor: 'background.paper' } }}>
            {label}
          </Button>
        </Tooltip>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ p: 0, '& .MuiList-root': { p: 0 } }}
          elevation={6}
        >
          <MenuList dense sx={{ p: 0 }}>
            <MenuItem selected={legendMode === 'overTime'} onClick={() => { setLegendMode('overTime'); setAnchorEl(null) }} sx={{ px: 0.5 }}>
              Flood depth over time
            </MenuItem>
            <MenuItem selected={legendMode === 'max'} onClick={() => { setLegendMode('max'); setAnchorEl(null) }} sx={{ px: 0.5 }}>
              Max flood depth
            </MenuItem>
          </MenuList>
        </Menu>
      </>
    )
});
